import {
  type AdresseTrouvee,
  type ClientTrouve,
  type CommuneTrouvee,
  LONGUEUR_MAX_RECHERCHE,
  LONGUEUR_MIN_RECHERCHE,
  type PersonneTrouvee,
  type RechercheReponse,
  rechercheReponseSchema,
  ressembleAUneAdresse,
} from '@carte/contrats/recherche';
import { NOMS_PAYS } from '@carte/domaine/geocodage/pays';
import { LIBELLES_STATUT } from '@carte/domaine/statut';
import * as Dialog from '@radix-ui/react-dialog';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Command } from 'cmdk';
import { Building2, MapPin, MapPinned, Search, TriangleAlert, User, X } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { ErreurApi, obtenir } from '../api';
import { BoutonRecherche } from './BoutonRecherche';
import { demanderCentrage } from './destination';
import { EVENEMENT_OUVRIR_PALETTE } from './ouvrir';

const DELAI_FRAPPE_MS = 150;

type Mode = 'auto' | 'adresse';

/** Vrai quand la cible d'un événement clavier est un champ de saisie (le raccourci `/` doit alors s'écrire). */
function estChampDeSaisie(cible: EventTarget | null): boolean {
  if (!(cible instanceof HTMLElement)) return false;
  if (cible.isContentEditable) return true;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(cible.tagName);
}

function useSaisieDifferee(valeur: string, delai: number): string {
  const [differee, setDifferee] = useState(valeur);
  useEffect(() => {
    const t = setTimeout(() => setDifferee(valeur), delai);
    return () => clearTimeout(t);
  }, [valeur, delai]);
  return differee;
}

const CLASSE_ITEM =
  'flex min-h-11 cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm text-texte data-[selected=true]:bg-accent/15 data-[selected=true]:outline data-[selected=true]:outline-1 data-[selected=true]:outline-accent/40';
const CLASSE_GROUPE =
  '[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-doux';

function Ligne({
  icone,
  titre,
  detail,
  etiquette,
}: {
  icone: ReactNode;
  titre: string;
  detail?: string;
  etiquette?: string;
}) {
  return (
    <>
      <span className="shrink-0 text-doux" aria-hidden>
        {icone}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{titre}</span>
        {detail ? <span className="block truncate text-xs text-doux">{detail}</span> : null}
      </span>
      {etiquette ? (
        <span className="shrink-0 rounded-full border border-bord px-2 py-0.5 text-xs text-doux">
          {etiquette}
        </span>
      ) : null}
    </>
  );
}

function detailClient(c: ClientTrouve): string | undefined {
  const lieu = [c.codePostal, c.ville].filter(Boolean).join(' ');
  return lieu || undefined;
}

function etiquetteClient(c: ClientTrouve): string | undefined {
  if (!c.localise) return 'Non localisé';
  return c.statut ? LIBELLES_STATUT[c.statut] : undefined;
}

function detailPersonne(p: PersonneTrouvee): string {
  const parties: string[] = [];
  if (p.nbClientsMentor > 0)
    parties.push(`${p.nbClientsMentor} client${p.nbClientsMentor > 1 ? 's' : ''} en tant que mentor`);
  if (p.nbClientsConsultant > 0)
    parties.push(
      `${p.nbClientsConsultant} client${p.nbClientsConsultant > 1 ? 's' : ''} en tant que consultant`,
    );
  return parties.join(', ') || 'Aucun client';
}

/**
 * Palette de recherche (cmdk), ouverte par `Ctrl K` ou `/` (SPEC 3, point 9).
 * Groupes : clients, personnes, communes, adresses. Un client ouvre sa fiche, une personne filtre la carte,
 * une commune ou une adresse centre la carte. L'action "Chercher comme adresse" force le mode adresse.
 *
 * `bouton` affiche un bouton flottant d'ouverture (utile tant que la coquille n'en porte pas).
 */
export function Palette({ bouton = true }: { bouton?: boolean }) {
  const navigate = useNavigate();
  const [ouvert, setOuvert] = useState(false);
  const [saisie, setSaisie] = useState('');
  const [mode, setMode] = useState<Mode>('auto');
  const [choix, setChoix] = useState({ base: '', valeur: '' });
  const q = useSaisieDifferee(saisie.trim(), DELAI_FRAPPE_MS);
  const assezLong = q.length >= LONGUEUR_MIN_RECHERCHE;

  const changerEtat = useCallback((valeur: boolean) => {
    setOuvert(valeur);
    if (!valeur) {
      setSaisie('');
      setMode('auto');
    }
  }, []);

  useEffect(() => {
    const surTouche = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOuvert((o) => !o);
        return;
      }
      if (e.key === '/' && !e.ctrlKey && !e.metaKey && !e.altKey && !estChampDeSaisie(e.target)) {
        e.preventDefault();
        setOuvert(true);
      }
    };
    const surOuverture = () => setOuvert(true);
    window.addEventListener('keydown', surTouche);
    window.addEventListener(EVENEMENT_OUVRIR_PALETTE, surOuverture);
    return () => {
      window.removeEventListener('keydown', surTouche);
      window.removeEventListener(EVENEMENT_OUVRIR_PALETTE, surOuverture);
    };
  }, []);

  // Une saisie qui ressemble à une adresse part en deux requêtes parallèles : les résultats de la base
  // s'affichent tout de suite, sans attendre la Géoplateforme (service externe, plus lent).
  const separer = mode === 'auto' && ressembleAUneAdresse(q);
  const requete = useQuery({
    queryKey: ['recherche', q, separer ? 'base' : mode],
    queryFn: () =>
      obtenir(
        '/api/recherche',
        rechercheReponseSchema,
        new URLSearchParams({ q, mode: separer ? 'base' : mode }),
      ),
    enabled: ouvert && assezLong,
    placeholderData: keepPreviousData,
    retry: false,
  });
  const requeteAdresses = useQuery({
    queryKey: ['recherche', q, 'adresse'],
    queryFn: () =>
      obtenir('/api/recherche', rechercheReponseSchema, new URLSearchParams({ q, mode: 'adresse' })),
    enabled: ouvert && assezLong && separer,
    placeholderData: keepPreviousData,
    retry: false,
  });

  const base = assezLong ? requete.data : undefined;
  const donnees: RechercheReponse | undefined =
    base && separer
      ? {
          ...base,
          adresses: requeteAdresses.data?.adresses ?? [],
          adressesIndisponibles: requeteAdresses.data?.adressesIndisponibles ?? false,
        }
      : base;

  // Le premier résultat affiché est toujours présélectionné, pour que Entrée ouvre le meilleur résultat.
  // Quand les adresses arrivent après les résultats de la base (requête séparée), elles ne déplacent
  // pas la présélection : Entrée ne doit pas changer de cible sous les doigts de l'utilisateur.
  const valeurBase = base && separer ? valeurPremierResultat(base) : '';
  const premiereValeur =
    valeurBase && valeurBase !== 'action-adresse'
      ? valeurBase
      : donnees
        ? valeurPremierResultat(donnees)
        : '';
  // La valeur choisie à la flèche ne vaut que pour le jeu de résultats où elle a été choisie ; dès que
  // la présélection change, elle redevient le premier résultat, sans attendre un effet (Entrée
  // pressée juste après l'affichage doit déjà viser le bon résultat).
  const selection = choix.base === premiereValeur ? choix.valeur : premiereValeur;
  const enAttenteFrappe = saisie.trim() !== q;
  const chargement =
    assezLong && (requete.isFetching || (separer && requeteAdresses.isFetching) || enAttenteFrappe);
  const nbResultats = donnees
    ? donnees.clients.length + donnees.personnes.length + donnees.communes.length + donnees.adresses.length
    : 0;

  const aller = (action: () => void) => {
    changerEtat(false);
    action();
  };

  const ouvrirClient = (c: ClientTrouve) =>
    aller(() => void navigate({ to: '/clients/$id', params: { id: String(c.id) } }));

  const filtrerParPersonne = (p: PersonneTrouvee) =>
    aller(
      () =>
        void navigate({
          to: '/',
          search:
            p.nbClientsMentor > 0 || p.nbClientsConsultant === 0 ? { mentor: p.cle } : { consultant: p.cle },
        }),
    );

  const centrer = (lon: number, lat: number, zoom: number, libelle: string) =>
    aller(() => {
      void navigate({ to: '/' }).then(() => demanderCentrage({ lon, lat, zoom, libelle }));
    });

  const messageErreur =
    requete.error instanceof ErreurApi && requete.error.statut === 401
      ? 'Votre session a expiré. Reconnectez-vous pour continuer.'
      : 'La recherche est momentanément indisponible.';

  const groupeAdresses = donnees && donnees.adresses.length > 0 && (
    <Command.Group heading="Adresses" className={CLASSE_GROUPE}>
      {donnees.adresses.map((a: AdresseTrouvee) => (
        <Command.Item
          key={`${a.libelle}-${a.lon}-${a.lat}`}
          value={`adresse-${a.lon}-${a.lat}-${a.libelle}`}
          onSelect={() => centrer(a.lon, a.lat, 17, a.libelle)}
          className={CLASSE_ITEM}
        >
          <Ligne icone={<MapPinned size={18} />} titre={a.libelle} detail="Voir sur la carte" />
        </Command.Item>
      ))}
    </Command.Group>
  );

  return (
    <>
      {bouton ? <BoutonRecherche className="fixed top-3 right-3 z-30" /> : null}
      <Dialog.Root open={ouvert} onOpenChange={changerEtat}>
        <Dialog.Portal>
          <Dialog.Overlay className="voile fixed inset-0 z-40 bg-black/40" />
          <Dialog.Content
            aria-describedby="palette-aide"
            className="surgissement-vif origin-top [--decalage:-6px] fixed inset-x-3 top-3 z-50 flex max-h-[calc(100dvh-1.5rem)] flex-col overflow-hidden rounded-xl border border-bord bg-surface text-texte shadow-2xl md:top-[10vh] md:left-1/2 md:w-full md:max-w-xl md:-translate-x-1/2 md:max-h-[75vh]"
          >
            <Dialog.Title className="sr-only">Recherche</Dialog.Title>
            <Dialog.Description id="palette-aide" className="sr-only">
              Rechercher un client, une personne, une commune ou une adresse. Flèches pour choisir, Entrée
              pour ouvrir, Échap pour fermer.
            </Dialog.Description>
            <Command
              label="Recherche"
              shouldFilter={false}
              loop
              value={selection}
              onValueChange={(valeur) => setChoix({ base: premiereValeur, valeur })}
              className="flex min-h-0 flex-1 flex-col"
            >
              <div className="flex items-center gap-2 border-b border-bord px-3">
                <Search size={18} className="shrink-0 text-doux" aria-hidden />
                <Command.Input
                  value={saisie}
                  onValueChange={setSaisie}
                  maxLength={LONGUEUR_MAX_RECHERCHE}
                  placeholder={
                    mode === 'adresse' ? 'Saisir une adresse' : 'Client, personne, ville ou adresse'
                  }
                  aria-label="Saisie de la recherche"
                  autoFocus
                  autoComplete="off"
                  spellCheck={false}
                  enterKeyHint="search"
                  className="h-12 min-w-0 flex-1 bg-transparent text-base text-texte outline-none placeholder:text-doux"
                />
                <Dialog.Close
                  aria-label="Fermer la recherche"
                  className="cible inline-flex items-center justify-center rounded-lg text-doux hover:text-texte"
                >
                  <X size={18} aria-hidden />
                </Dialog.Close>
              </div>

              {mode === 'adresse' ? (
                <div className="flex items-center justify-between gap-2 border-b border-bord bg-accent/10 px-3 py-2 text-sm">
                  <span>Recherche d'adresse</span>
                  <button
                    type="button"
                    onClick={() => setMode('auto')}
                    className="rounded-md px-2 py-1 text-accent underline underline-offset-2"
                  >
                    Revenir à la recherche générale
                  </button>
                </div>
              ) : null}

              <Command.List className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-2">
                {!assezLong ? (
                  <p className="px-3 py-6 text-center text-sm text-doux">
                    Tapez au moins {LONGUEUR_MIN_RECHERCHE} caractères : nom d'un client, d'une personne,
                    d'une ville, code postal ou adresse.
                  </p>
                ) : null}

                {donnees?.ressembleAdresse ? groupeAdresses : null}

                {donnees && donnees.clients.length > 0 ? (
                  <Command.Group heading="Clients" className={CLASSE_GROUPE}>
                    {donnees.clients.map((c) => (
                      <Command.Item
                        key={c.id}
                        value={`client-${c.id}`}
                        onSelect={() => ouvrirClient(c)}
                        className={CLASSE_ITEM}
                      >
                        <Ligne
                          icone={<Building2 size={18} />}
                          titre={c.raisonSociale}
                          detail={detailClient(c)}
                          etiquette={etiquetteClient(c)}
                        />
                      </Command.Item>
                    ))}
                  </Command.Group>
                ) : null}

                {donnees && donnees.personnes.length > 0 ? (
                  <Command.Group heading="Personnes" className={CLASSE_GROUPE}>
                    {donnees.personnes.map((p) => (
                      <Command.Item
                        key={p.cle}
                        value={`personne-${p.cle}`}
                        onSelect={() => filtrerParPersonne(p)}
                        className={CLASSE_ITEM}
                      >
                        <Ligne icone={<User size={18} />} titre={p.nom} detail={detailPersonne(p)} />
                      </Command.Item>
                    ))}
                  </Command.Group>
                ) : null}

                {donnees && donnees.communes.length > 0 ? (
                  <Command.Group heading="Communes" className={CLASSE_GROUPE}>
                    {donnees.communes.map((m: CommuneTrouvee) => (
                      <Command.Item
                        key={m.id}
                        value={`commune-${m.id}`}
                        onSelect={() => centrer(m.lon, m.lat, 12, m.nom)}
                        className={CLASSE_ITEM}
                      >
                        <Ligne
                          icone={<MapPin size={18} />}
                          titre={m.nom}
                          detail={[m.codePostal, m.departement, m.pays === 'FR' ? null : NOMS_PAYS[m.pays]]
                            .filter(Boolean)
                            .join(', ')}
                        />
                      </Command.Item>
                    ))}
                  </Command.Group>
                ) : null}

                {!donnees?.ressembleAdresse ? groupeAdresses : null}

                {donnees && mode === 'auto' ? (
                  <Command.Group heading="Actions" className={CLASSE_GROUPE}>
                    <Command.Item
                      value="action-adresse"
                      onSelect={() => setMode('adresse')}
                      className={CLASSE_ITEM}
                    >
                      <Ligne icone={<MapPinned size={18} />} titre={`Chercher « ${q} » comme adresse`} />
                    </Command.Item>
                  </Command.Group>
                ) : null}
              </Command.List>

              <div
                className="min-h-6 border-t border-bord px-3 py-2 text-sm"
                role="status"
                aria-live="polite"
              >
                {requete.isError && assezLong ? (
                  <div className="flex items-center justify-between gap-2 text-erreur">
                    <span className="flex items-center gap-2">
                      <TriangleAlert size={16} aria-hidden />
                      {messageErreur}
                    </span>
                    {requete.error instanceof ErreurApi && requete.error.statut === 401 ? null : (
                      <button
                        type="button"
                        onClick={() => void requete.refetch()}
                        className="rounded-md px-2 py-1 underline underline-offset-2"
                      >
                        Réessayer
                      </button>
                    )}
                  </div>
                ) : chargement && !donnees ? (
                  <span className="text-doux">Recherche en cours...</span>
                ) : donnees && nbResultats === 0 && !chargement ? (
                  <span className="text-doux">
                    Aucun résultat pour « {q} ».
                    {donnees.adressesIndisponibles
                      ? " La recherche d'adresses est indisponible pour le moment."
                      : ' Essayez une autre orthographe ou cherchez-le comme adresse.'}
                  </span>
                ) : donnees?.adressesIndisponibles ? (
                  <span className="flex items-center gap-2 text-alerte">
                    <TriangleAlert size={16} aria-hidden />
                    La recherche d'adresses est indisponible pour le moment.
                  </span>
                ) : donnees ? (
                  <span className="text-doux">
                    {nbResultats} résultat{nbResultats > 1 ? 's' : ''}
                  </span>
                ) : (
                  <span className="hidden text-doux md:inline">
                    Flèches pour choisir, Entrée pour ouvrir, Échap pour fermer
                  </span>
                )}
              </div>
            </Command>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}

/** Valeur cmdk du premier élément affiché, dans l'ordre des groupes du rendu. */
function valeurPremierResultat(d: RechercheReponse): string {
  const adresse =
    d.adresses[0] && `adresse-${d.adresses[0].lon}-${d.adresses[0].lat}-${d.adresses[0].libelle}`;
  const autre = d.clients[0]
    ? `client-${d.clients[0].id}`
    : d.personnes[0]
      ? `personne-${d.personnes[0].cle}`
      : d.communes[0]
        ? `commune-${d.communes[0].id}`
        : '';
  if (d.ressembleAdresse) return adresse || autre || 'action-adresse';
  return autre || adresse || 'action-adresse';
}
